import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const bannerVariants = cva('rounded-2xl border w-full', {
  variants: {
    variant: {
      default: 'border-border bg-input/50',
      primary: 'border-[#f97316]/30 bg-[#f97316]/10',
      success: 'border-emerald-500/30 bg-emerald-500/10',
      warning: 'border-yellow-500/30 bg-yellow-500/10',
      danger: 'border-red-500/30 bg-red-500/10',
      info: 'border-blue-500/30 bg-blue-500/10',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const bannerIconVariants = cva('flex items-center justify-center rounded-full shrink-0', {
  variants: {
    variant: {
      default: 'bg-elevated',
      primary: 'bg-[#f97316]/20',
      success: 'bg-emerald-500/20',
      warning: 'bg-yellow-500/20',
      danger: 'bg-red-500/20',
      info: 'bg-blue-500/20',
    },
    size: {
      default: 'size-9',
      compact: 'size-7',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

const bannerTitleVariants = cva('font-semibold', {
  variants: {
    variant: {
      default: 'text-foreground',
      primary: 'text-[#f97316]',
      success: 'text-emerald-400',
      warning: 'text-yellow-400',
      danger: 'text-red-400',
      info: 'text-blue-400',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const bannerIconColorVariants = {
  default: 'text-muted-foreground',
  primary: 'text-[#f97316]',
  success: 'text-emerald-400',
  warning: 'text-yellow-400',
  danger: 'text-red-400',
  info: 'text-blue-400',
}

export interface BannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bannerVariants> {
  /** Icon element to display */
  icon?: React.ReactNode
  /** Banner title */
  title: string
  /** Optional description text or rich content */
  description?: React.ReactNode
  /** Optional action buttons/links */
  actions?: React.ReactNode
  /** Optional content rendered below the header row */
  children?: React.ReactNode
  /** `compact` trims padding and icon size for a slimmer, single-line banner */
  size?: 'default' | 'compact'
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    { icon, title, description, actions, variant, size = 'default', className, children, ...props },
    ref
  ) => {
    const iconColor = bannerIconColorVariants[variant ?? 'default']

    return (
      <div ref={ref} className={cn(bannerVariants({ variant }), className)} {...props}>
        <div
          className={cn('flex flex-wrap items-center gap-3', size === 'compact' ? 'p-2.5' : 'p-4')}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {icon && (
              <div className={cn(bannerIconVariants({ variant, size }), iconColor)}>{icon}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className={cn(bannerTitleVariants({ variant }), size === 'compact' && 'text-sm')}>
                {title}
              </p>
              {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
        {children}
      </div>
    )
  }
)

Banner.displayName = 'Banner'
